import {
  useState,
  useEffect
} from "react";
import {
  useNavigate,
  useParams,
  Link
} from "react-router-dom";

import StudentForm from "../components/StudentForm";

import {
  createStudent,
  getStudentById,
  updateStudent
} from "../api/student/studentApi";

import {
  createStudentForPortal,
  getStudentByIdForPortal,
  updateStudentForPortal
} from "../api/student/studentPortalApi";

import {
  Card,
  CardContent,
  Button,
  Skeleton
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const AddStudent = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [student, setStudent] = useState(null);

  const toast = useToast();

  const homeUrl = isPortal ? "/staff-home" : "/school-home";
  const studentListUrl = isPortal ? "/staff-panel/students" : "/students";

  /*
  |--------------------------------------------------------------------------
  | LOAD STUDENT (EDIT MODE)
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!isEdit) return;

    const fetchStudent = async () => {
      try {
        setLoading(true);

        const data = isPortal
          ? await getStudentByIdForPortal(id)
          : await getStudentById(id);

        setStudent(data);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to load student"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id, isEdit, isPortal]);

  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);

      if (isEdit) {
        if (isPortal) {
          await updateStudentForPortal(id, formData);
        } else {
          await updateStudent(id, formData);
        }

        navigate(studentListUrl);
        return;
      }

      const data = isPortal
        ? await createStudentForPortal(formData)
        : await createStudent(formData);

      setCredentials(data.credentials);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (isEdit
            ? "Failed to update student"
            : "Failed to create student")
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | BACK
  |--------------------------------------------------------------------------
  */

  const handleBack = () => {
    navigate(studentListUrl);
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING SCREEN
  |--------------------------------------------------------------------------
  */

  if (loading && isEdit && !student) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-4">
        <Skeleton height={24} width="30%" />
        <div className="space-y-4 pt-4">
          <Skeleton height={20} width="40%" />
          <Skeleton height={220} width="100%" />
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6 bg-neutral-50 dark:bg-neutral-950 min-h-screen">
      {/* Minimal Top Breadcrumb Header (Borderless) */}
      <div className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <nav className="mb-1 flex items-center gap-2 text-xs font-extrabold text-neutral-400 dark:text-neutral-500">
            <Link to={homeUrl} className="hover:text-indigo-600 transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to={studentListUrl} className="hover:text-indigo-600 transition-colors">
              Student Directory
            </Link>
            <span>/</span>
            <span className="text-neutral-800 dark:text-neutral-100">
              {isEdit ? "Edit Student" : "Add Student"}
            </span>
          </nav>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            {isEdit ? "Edit Student Profile" : "Add New Student"}
          </h1>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="w-fit text-xs font-extrabold text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:text-neutral-100"
        >
          Back to List
        </Button>
      </div>

      {/* Error Feedback */}
      

      {/* Success Credentials Banner (Tabular Form, Borderless) */}
      {!isEdit && credentials && (
        <Card className="bg-emerald-50/50 rounded-2xl">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-xs font-extrabold text-emerald-800 uppercase tracking-wider">
              Student Account Provisioned
            </h3>

            <div className="overflow-x-auto rounded-xl bg-white dark:bg-neutral-900">
              <table className="w-full text-left text-xs border-collapse">
                <tbody>
                  <tr className="bg-white dark:bg-neutral-900">
                    <td className="w-1/3 py-3 px-4 font-bold text-neutral-400 dark:text-neutral-500">
                      Student ID
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-neutral-800 dark:text-neutral-100">
                      {credentials.studentId}
                    </td>
                  </tr>
                  <tr className="bg-neutral-50/50 dark:bg-neutral-950">
                    <td className="w-1/3 py-3 px-4 font-bold text-neutral-400 dark:text-neutral-500">
                      Username
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-neutral-800 dark:text-neutral-100">
                      {credentials.username}
                    </td>
                  </tr>
                  <tr className="bg-white dark:bg-neutral-900">
                    <td className="w-1/3 py-3 px-4 font-bold text-neutral-400 dark:text-neutral-500">
                      Password
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-neutral-800 dark:text-neutral-100">
                      {credentials.password}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
              Save these credentials carefully — the password will not be shown again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main Form Area */}
      <div className="pt-2">
        <StudentForm
          onSubmit={handleSubmit}
          loading={loading}
          isPortal={isPortal}
          initialData={student}
          isEdit={isEdit}
        />
      </div>
    </div>
  );
};

export default AddStudent;